import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "./ConfirmDialog";
import AdminPagination from "@/components/admin/AdminPagination";
import AdminLayout from "../../../../components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import { AdminSearchForm } from "@/components/admin/AdminTableToolbar";
import SimpleSelect from "../../../../components/base/SimpleSelect";
import SimpleMultiSelect from "../../../../components/base/SimpleMultiSelect";
import CreatableMultiSelect from "../../../../components/base/CreatableMultiSelect";
import ModalCloseButton from "@/components/base/ModalCloseButton";
import {
  MODAL_FOOTER_CANCEL_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_STYLE,
} from "@/components/base/modalFooterButtonStyles";
import { docCategoryApi, docTagApi, type DocTaxonomyItem } from "@/services/docTaxonomy";
import { REGION_LOCKED_CATEGORY, type UserRegion } from "@/services/users";
import {
  createSourcePathAsk,
  deleteSourcePathAsk,
  getGlobalExcludedKeywords,
  listSourcePathAsks,
  updateGlobalExcludedKeywords,
  updateSourcePathAsk,
  type SourcePathAskUserScope,
} from "../../../../services/sourcePathAsk";
import { Tooltip, App } from "antd";
import { findIncludeExcludePathConflict } from "./pathValidation";
import { formatSharePointSitePreview, normalizePath, type PathItem } from "./pathSettingsUtils";
import GlobalExcludedKeywords from "./GlobalExcludedKeywords";
import SourceFormPageLayout, {
  SourceAuthorizationNotice,
  SourceFormField,
} from "../components/SourceFormPageLayout";

interface DocTagGroup {
  tagType: string;
  tags: string[];
}

const INLINE_ADD_BUTTON_CLASS =
  "inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2.5 text-[11px] font-medium text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700";
const COMPACT_REMOVE_BUTTON_CLASS =
  "flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500";
const SOURCE_LIST_PAGE_SIZE = 20;

// ── Preset tag values by tag name ──────────────────────────
const TAG_PRESET_VALUES: Record<string, string[]> = {
  Indication: ["Oncology"],
  "Study Code": ["PACIFIC"],
  Language: ["English"],
  Audience: ["CRA"],
};

function hasPresetValues(tagName: string): boolean {
  return !!TAG_PRESET_VALUES[tagName] && TAG_PRESET_VALUES[tagName].length > 0;
}

function getPresetValues(tagName: string): string[] {
  return TAG_PRESET_VALUES[tagName] ?? [];
}

/** AddPathModal 提交的表单数据。Source Type / 分类 / 标签一律以 ID 传出 */
export interface SourceFormPayload {
  site: string;
  drive: string;
  includedPaths: string[];
  excludedPaths: string[];
  remark: string;
  sourceTypeId: string;
  docCategoryIds: string[];
  docTagBindings: Array<{ tagId: string; presetValues: string[] }>;
  agentTips: string;
  region: UserRegion;
  userScope: SourcePathAskUserScope;
}

function createPathFields(paths: string[], emptyValue = "") {
  return paths.length > 0 ? [...paths] : [emptyValue];
}

function compactPathFields(paths: string[]) {
  return paths.map((path) => path.trim()).filter(Boolean);
}

export function PathListDisplay({ paths, className }: { paths: string[]; className: string }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {paths.map((path, index) => (
        <span
          key={`${path}-${index}`}
          className={
            `inline-flex items-center text-[10px] text-gray-500 font-mono rounded px-1.5 py-0.5 truncate` +
            ` ${className}`
          }
        >
          {path}
        </span>
      ))}
    </div>
  );
}

function FieldHelpTooltip({ text }: { text: string }) {
  return (
    <span className="group relative inline-flex items-center">
      <button
        type="button"
        aria-label={text}
        className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gray-100 text-gray-500 cursor-default transition-colors hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-200"
      >
        <i className="ri-question-mark text-[9px] leading-none"></i>
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden w-72 max-w-[calc(100vw-4rem)] rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block group-focus-within:block"
      >
        {text}
        <span className="absolute left-2 top-full h-2 w-2 -translate-y-1/2 rotate-45 bg-gray-900"></span>
      </span>
    </span>
  );
}

function SourceRegionSelector({
  region,
  name,
  onChange,
}: {
  region: UserRegion;
  name: string;
  onChange: (region: UserRegion) => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      role="radiogroup"
      aria-label={t("sourceForm.region")}
      className="flex flex-wrap gap-5 py-2"
    >
      {(["Mainland", "HK"] as UserRegion[]).map((option) => (
        <label
          key={option}
          className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
        >
          <input
            type="radio"
            name={name}
            value={option}
            checked={region === option}
            onChange={() => onChange(option)}
            className="h-4 w-4 accent-[#830051]"
          />
          <span>
            {option === "Mainland"
              ? t("sourceForm.region.mainland")
              : t("sourceForm.region.hongKong")}
          </span>
        </label>
      ))}
    </div>
  );
}

function PathInputList({
  label,
  values,
  placeholder,
  addLabel,
  removeLabel,
  hasConflict = false,
  onChange,
}: {
  label: string;
  values: string[];
  placeholder: string;
  addLabel: string;
  removeLabel: string;
  hasConflict?: boolean;
  onChange: (values: string[]) => void;
}) {
  const updateValue = (index: number, value: string) => {
    onChange(values.map((path, pathIndex) => (pathIndex === index ? value : path)));
  };

  const removeValue = (index: number) => {
    if (index === values.length - 1) return;

    onChange(values.filter((_, pathIndex) => pathIndex !== index));
  };

  return (
    <div className="flex flex-col gap-2">
      {values.map((value, index) => {
        const invalid = !!value.trim() && !value.trim().startsWith("/");
        const isLast = index === values.length - 1;

        return (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              aria-label={`${label} ${index + 1}`}
              aria-invalid={invalid || hasConflict}
              value={value}
              onChange={(event) => updateValue(index, event.target.value)}
              placeholder={placeholder}
              className={`min-w-0 flex-1 rounded-lg border px-3.5 py-2.5 font-mono text-sm outline-none transition-colors placeholder:font-sans ${
                invalid || hasConflict
                  ? "border-red-300 focus:border-red-400"
                  : "border-gray-200 focus:border-gray-400"
              }`}
            />
            {isLast && (
              <button
                type="button"
                aria-label={`${addLabel} ${label}`}
                onClick={() => onChange([...values, ""])}
                className={INLINE_ADD_BUTTON_CLASS}
              >
                <i className="ri-add-line text-sm leading-none"></i>
                <span>{addLabel}</span>
              </button>
            )}
            {!isLast && (
              <button
                type="button"
                aria-label={`${removeLabel} ${label} ${index + 1}`}
                onClick={() => removeValue(index)}
                className={COMPACT_REMOVE_BUTTON_CLASS}
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function getResponseMessage(error: unknown) {
  if (!axios.isAxiosError<{ message?: string }>(error)) {
    return undefined;
  }

  return error.response?.data?.message?.trim() || undefined;
}

export function AddPathModal({
  mode = "add",
  presentation = "modal",
  initialSite = "",
  initialDrive = "",
  initialIncludedPaths = [],
  initialExcludedPaths = [],
  initialDocCategoryIds = [],
  initialDocTagBindings = [],
  initialSourceTypeId = "",
  initialRemark = "",
  initialAgentTips = "",
  initialRegion = "Mainland",
  initialUserScope = 0,
  error,
  submitting,
  onConfirm,
  onCancel,
}: {
  mode?: "add" | "edit";
  presentation?: "modal" | "page";
  initialSite?: string;
  initialDrive?: string;
  initialIncludedPaths?: string[];
  initialExcludedPaths?: string[];
  initialDocCategoryIds?: string[];
  initialDocTagBindings?: Array<{ tagId: string; presetValues: string[] }>;
  initialSourceTypeId?: string;
  initialRemark?: string;
  initialAgentTips?: string;
  initialRegion?: UserRegion;
  initialUserScope?: SourcePathAskUserScope;
  error?: string;
  submitting?: boolean;
  onConfirm: (payload: SourceFormPayload) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const isPage = presentation === "page";
  const [site, setSite] = useState(initialSite);
  const [drive, setDrive] = useState(initialDrive);
  const [includedPathFields, setIncludedPathFields] = useState(() =>
    createPathFields(initialIncludedPaths, "/"),
  );
  const [excludedPathFields, setExcludedPathFields] = useState(() =>
    createPathFields(initialExcludedPaths),
  );

  const [docCategories, setDocCategories] = useState<string[]>([]);
  const [docTagGroups, setDocTagGroups] = useState<DocTagGroup[]>(
    mode === "add" ? [{ tagType: "", tags: [] }] : [],
  );
  const [sourceTypeId, setSourceTypeId] = useState(initialSourceTypeId);
  const [remark, setRemark] = useState(initialRemark);
  const [agentTips, setAgentTips] = useState(initialAgentTips);
  const [region, setRegion] = useState<UserRegion>(initialRegion);
  const [userScope, setUserScope] = useState<SourcePathAskUserScope>(initialUserScope);

  // 分类 / 标签目录快照。MultiSelect 仍按「名称」工作（不改组件），
  // 提交时用同一份快照把名称换回 ID——用户只能选到快照里的名称，故映射必然命中。
  const [categoryCatalog, setCategoryCatalog] = useState<DocTaxonomyItem[]>([]);
  const [tagCatalog, setTagCatalog] = useState<DocTaxonomyItem[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const incompatibleCategoryName =
    REGION_LOCKED_CATEGORY[region === "Mainland" ? "HK" : "Mainland"];
  const categoryOptions = categoryCatalog
    .filter((category) => category.name !== incompatibleCategoryName)
    .map((category) => category.name);
  const typeOptions = categoryOptions;
  const sourceType =
    categoryCatalog.find(
      (category) => category.id === sourceTypeId && category.name !== incompatibleCategoryName,
    )?.name ?? "";
  // Source 可绑定全部已定义标签；是否可编辑 / 删除不影响标签的可选性。
  const tagOptions = tagCatalog.map((tg) => tg.name);
  const tagPrompts = Object.fromEntries(tagCatalog.map((tg) => [tg.name, tg.prompt ?? ""]));

  useEffect(() => {
    let cancelled = false;

    async function loadMeta() {
      setLoadingMeta(true);
      try {
        const [categories, tags] = await Promise.all([docCategoryApi.list(), docTagApi.list()]);
        if (cancelled) return;

        const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));
        setCategoryCatalog(sortedCategories);
        if (!initialSourceTypeId) {
          setSourceTypeId(sortedCategories.find((c) => c.name === "General Docs")?.id ?? "");
        }

        // 全量存入（含 Custom 与 System 标签），供下拉选择及 ID↔名称映射使用。
        setTagCatalog([...tags].sort((a, b) => a.name.localeCompare(b.name)));
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    }

    loadMeta();

    return () => {
      cancelled = true;
    };
  }, [initialSourceTypeId]);

  // catalog 到位后把入参的 ID 还原成名称——表单内部一律按名称工作
  useEffect(() => {
    if (categoryCatalog.length === 0) return;
    const nameById = new Map(categoryCatalog.map((c) => [c.id, c.name]));
    setDocCategories(
      initialDocCategoryIds.map((id) => nameById.get(id)).filter((n): n is string => !!n),
    );
    // initialDocCategoryIds 由父组件按 editTarget 传入，随弹窗生命周期固定，无需进依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCatalog]);

  // Region 变化（或分类目录到货）后，清掉与当前 Region 互斥的锁定分类，
  // 并把落到互斥分类上的 Source Type 改回本 Region 的锁定分类。
  // 切换 Region 只需 setRegion，本 effect 会接管后续收敛，不要在事件处理里重复一遍。
  useEffect(() => {
    if (categoryCatalog.length === 0) return;

    setDocCategories((current) =>
      current.includes(incompatibleCategoryName)
        ? current.filter((name) => name !== incompatibleCategoryName)
        : current,
    );
    setSourceTypeId((currentId) => {
      const currentCategory = categoryCatalog.find((category) => category.id === currentId);
      if (currentCategory?.name !== incompatibleCategoryName) return currentId;
      return (
        categoryCatalog.find((category) => category.name === REGION_LOCKED_CATEGORY[region])?.id ??
        ""
      );
    });
  }, [categoryCatalog, incompatibleCategoryName, region]);

  useEffect(() => {
    if (tagCatalog.length === 0) return;
    const nameById = new Map(tagCatalog.map((tg) => [tg.id, tg.name]));
    const groups = initialDocTagBindings
      .map((b) => ({ tagType: nameById.get(b.tagId), tags: b.presetValues }))
      .filter((g): g is DocTagGroup => !!g.tagType);
    setDocTagGroups(groups.length > 0 ? groups : mode === "add" ? [{ tagType: "", tags: [] }] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagCatalog]);

  const updateDocTagGroup = (index: number, tagType: string, tags: string[]) => {
    setDocTagGroups((prev) => {
      const next = [...prev];
      next[index] = { tagType, tags };
      return next;
    });
  };

  const addDocTagGroup = () => {
    setDocTagGroups((prev) => [...prev, { tagType: "", tags: [] }]);
  };

  const removeDocTagGroup = (index: number) => {
    setDocTagGroups((prev) => prev.filter((_, i) => i !== index));
  };

  const trimmedSite = site.trim();
  const trimmedDrive = drive.trim();
  const includedPaths = compactPathFields(includedPathFields);
  const excludedPaths = compactPathFields(excludedPathFields);
  const invalidIncludedPath = includedPaths.find((path) => !path.startsWith("/"));
  const invalidExcludedPath = excludedPaths.find((path) => !path.startsWith("/"));
  const pathInvalid = !!invalidIncludedPath || !!invalidExcludedPath;
  const pathConflict = pathInvalid
    ? null
    : findIncludeExcludePathConflict(includedPaths, excludedPaths);
  const pathConflictMessage =
    pathConflict?.type === "exact"
      ? t("pathSettings.exactPathConflict", { path: pathConflict.excludedPath })
      : pathConflict?.type === "excluded-ancestor"
        ? t("pathSettings.excludedAncestorConflict", {
            excludedPath: pathConflict.excludedPath,
            includedPath: pathConflict.includedPath,
          })
        : "";
  const hasIncludedPath = includedPaths.length > 0;
  const sourceTypeIsValid = categoryCatalog.some(
    (category) => category.id === sourceTypeId && category.name !== incompatibleCategoryName,
  );
  const canSubmit =
    !!trimmedSite &&
    hasIncludedPath &&
    sourceTypeIsValid &&
    !pathInvalid &&
    !pathConflict &&
    !submitting;
  const sitePreview = formatSharePointSitePreview(site);

  const handleSubmit = () => {
    if (!trimmedSite || !hasIncludedPath || !sourceTypeIsValid || submitting) return;
    if (pathInvalid || pathConflict) return;
    // 名称换回 ID：options 与 values 同源于 catalog 快照，故映射必然命中
    const categoryIdByName = new Map(categoryCatalog.map((c) => [c.name, c.id]));
    const tagIdByName = new Map(tagCatalog.map((tg) => [tg.name, tg.id]));

    onConfirm({
      site: trimmedSite,
      drive: trimmedDrive,
      includedPaths,
      excludedPaths,
      remark: remark.trim(),
      sourceTypeId,
      docCategoryIds: docCategories
        .map((name) => categoryIdByName.get(name))
        .filter((id): id is string => !!id),
      docTagBindings: docTagGroups
        .map((g) => ({ tagId: tagIdByName.get(g.tagType), presetValues: g.tags }))
        .filter((b): b is { tagId: string; presetValues: string[] } => !!b.tagId),
      agentTips: agentTips.trim(),
      region,
      userScope,
    });
  };

  if (isPage) {
    return (
      <SourceFormPageLayout
        title={mode === "edit" ? t("pathSettings.editTitle") : t("pathSettings.prereq.modalTitle")}
        onBack={onCancel}
        notice={<SourceAuthorizationNotice />}
        primaryLabel={mode === "edit" ? t("pathSettings.save") : t("pathSettings.add")}
        primaryDisabled={!canSubmit}
        submitting={submitting}
        onPrimary={handleSubmit}
      >
        <SourceFormField label={t("pathSettings.sourceSite")} required>
          <input
            type="text"
            value={site}
            onChange={(event) => setSite(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleSubmit()}
            placeholder={t("pathSettings.sourceSitePlaceholder")}
            autoFocus
            className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 font-mono text-sm outline-none transition-colors placeholder:font-sans focus:border-gray-400"
          />
          {sitePreview && (
            <p className="mt-1 break-all font-mono text-[10px] leading-snug text-gray-400">
              {sitePreview}
            </p>
          )}
        </SourceFormField>

        <SourceFormField label={t("sourceForm.remark")} hint={t("sourceForm.remarkHint")}>
          <input
            type="text"
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
            placeholder={t("sourceForm.remarkPlaceholder")}
            maxLength={500}
            className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-gray-400"
          />
        </SourceFormField>

        <SourceFormField
          label={
            <>
              {t("sourceForm.region")}
              <span className="text-red-400">*</span>
              <FieldHelpTooltip text={t("sourceForm.regionTooltip")} />
            </>
          }
        >
          <SourceRegionSelector region={region} name="source-region-page" onChange={setRegion} />
        </SourceFormField>

        {loadingMeta && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 lg:ml-[184px]">
            <div className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin text-sm text-amber-500" />
              <span className="text-xs text-amber-700">{t("sourceForm.loadingMeta")}</span>
            </div>
          </div>
        )}

        <SourceFormField label={t("sourceForm.docCategories")}>
          <SimpleMultiSelect
            label=""
            values={docCategories}
            options={categoryOptions}
            onChange={setDocCategories}
            placeholder={t("sourceForm.docCategoriesPlaceholder")}
            hideLabel
          />
          {categoryOptions.length === 0 && !loadingMeta && (
            <p className="mt-1 text-[11px] text-amber-600">{t("sourceForm.sourceTypeEmpty")}</p>
          )}
        </SourceFormField>

        <SourceFormField label={t("sourceForm.docTags")}>
          <div className="flex flex-col gap-3">
            {docTagGroups.map((group, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-40 flex-shrink-0">
                  <SimpleSelect
                    label=""
                    value={group.tagType}
                    options={tagOptions}
                    onChange={(value) => updateDocTagGroup(index, value, group.tags)}
                    placeholder={t("sourceForm.docTagsSelectTag")}
                    hideLabel
                  />
                </div>
                <div className="min-w-0 flex-1">
                  {hasPresetValues(group.tagType) ? (
                    <SimpleMultiSelect
                      label=""
                      values={group.tags}
                      options={getPresetValues(group.tagType)}
                      onChange={(values) => updateDocTagGroup(index, group.tagType, values)}
                      placeholder={t("sourceForm.docTagsSelectValues", {
                        tag: group.tagType.toLowerCase(),
                      })}
                      hideLabel
                      creatable
                    />
                  ) : (
                    <CreatableMultiSelect
                      label=""
                      values={group.tags}
                      options={[]}
                      onChange={(values) => updateDocTagGroup(index, group.tagType, values)}
                      placeholder={
                        group.tagType && tagPrompts[group.tagType]
                          ? `e.g. ${tagPrompts[group.tagType].split("。")[0].slice(0, 60)}...`
                          : t("sourceForm.docTagsTagValues")
                      }
                      hideLabel
                      hideClearInDropdown
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeDocTagGroup(index)}
                  className={`${COMPACT_REMOVE_BUTTON_CLASS} mt-[9px]`}
                  aria-label={t("sourceForm.docTagsRemoveGroup")}
                >
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addDocTagGroup}
              className={`${INLINE_ADD_BUTTON_CLASS} w-fit`}
            >
              <i className="ri-add-line text-sm leading-none" />
              <span>{t("sourceForm.docTagsAddTag")}</span>
            </button>
          </div>
        </SourceFormField>

        <SourceFormField label={t("pathSettings.sourceType")} required>
          <SimpleSelect
            label=""
            value={sourceType}
            options={typeOptions}
            onChange={(name) =>
              setSourceTypeId(categoryCatalog.find((category) => category.name === name)?.id ?? "")
            }
            placeholder={t("sourceForm.sourceTypePlaceholder")}
            hideLabel
          />
          {typeOptions.length === 0 && !loadingMeta && (
            <p className="mt-1 text-[11px] text-amber-600">{t("sourceForm.sourceTypeEmpty")}</p>
          )}
        </SourceFormField>

        <SourceFormField label={t("sourceForm.agentTips")} hint={t("sourceForm.agentTipsHint")}>
          <textarea
            value={agentTips}
            onChange={(event) => setAgentTips(event.target.value)}
            placeholder={t("sourceForm.agentTipsPlaceholder")}
            rows={4}
            maxLength={500}
            className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-gray-400"
          />
        </SourceFormField>

        <div className="border-t border-gray-100" />

        <SourceFormField
          label={t("pathSettings.sourceDrive")}
          hint={t("pathSettings.sourceDriveHint")}
        >
          <input
            type="text"
            value={drive}
            onChange={(event) => setDrive(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleSubmit()}
            placeholder={t("pathSettings.sourceDrivePlaceholder")}
            className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 font-mono text-sm outline-none transition-colors placeholder:font-sans focus:border-gray-400"
          />
        </SourceFormField>

        <SourceFormField label={t("pathSettings.includedPaths")} required>
          <PathInputList
            label={t("pathSettings.includedPaths")}
            values={includedPathFields}
            placeholder={t("pathSettings.includedPathsPlaceholder")}
            addLabel={t("pathSettings.addPathField")}
            removeLabel={t("pathSettings.removePathField")}
            onChange={setIncludedPathFields}
          />
          {(!hasIncludedPath || invalidIncludedPath || error) && (
            <p className="mt-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {!hasIncludedPath
                ? t("pathSettings.includedPathRequired")
                : invalidIncludedPath
                  ? t("pathSettings.pathMustStartWithSlash")
                  : error}
            </p>
          )}
        </SourceFormField>

        <SourceFormField label={t("pathSettings.excludedPaths")}>
          <PathInputList
            label={t("pathSettings.excludedPaths")}
            values={excludedPathFields}
            placeholder={t("pathSettings.excludedPathsPlaceholder")}
            addLabel={t("pathSettings.addPathField")}
            removeLabel={t("pathSettings.removePathField")}
            hasConflict={!!pathConflict}
            onChange={setExcludedPathFields}
          />
          {invalidExcludedPath && (
            <p className="mt-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {t("pathSettings.pathMustStartWithSlash")}
            </p>
          )}
          {pathConflictMessage && (
            <p className="mt-2 flex items-start gap-1.5 text-xs font-medium leading-relaxed text-red-500">
              <i className="ri-error-warning-line mt-0.5 shrink-0" />
              <span>{pathConflictMessage}</span>
            </p>
          )}
        </SourceFormField>

        <div className="border-t border-gray-100" />

        <SourceFormField label={t("sourceForm.userScope")}>
          <div
            role="radiogroup"
            aria-label={t("sourceForm.userScope")}
            className="flex flex-wrap gap-5 py-2"
          >
            {(
              [
                { value: 1, label: t("sourceForm.userScope.internalExternal") },
                { value: 0, label: t("sourceForm.userScope.internalOnly") },
              ] as Array<{ value: SourcePathAskUserScope; label: string }>
            ).map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="radio"
                  name="source-user-scope-page"
                  value={option.value}
                  checked={userScope === option.value}
                  onChange={() => setUserScope(option.value)}
                  className="h-4 w-4 accent-[#830051]"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </SourceFormField>
      </SourceFormPageLayout>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="mx-4 flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-sm">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg"
            style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
          >
            <i className="ri-add-circle-line text-base" style={{ color: "#830051" }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {mode === "edit" ? t("pathSettings.editTitle") : t("pathSettings.prereq.modalTitle")}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">{t("pathSettings.prereq.modalSubtitle")}</p>
          </div>
          <ModalCloseButton
            ariaLabel={t("common.close")}
            disabled={submitting}
            onClick={onCancel}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {/* Site Input */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              {t("pathSettings.sourceSite")}
            </label>
            <input
              type="text"
              value={site}
              onChange={(e) => setSite(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              placeholder={t("pathSettings.sourceSitePlaceholder")}
              autoFocus
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-gray-400 font-mono"
            />
            {sitePreview && (
              <p className="text-[10px] text-gray-400 font-mono break-all leading-snug">
                {sitePreview}
              </p>
            )}
          </div>

          {/* Remark */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">{t("sourceForm.remark")}</label>
            <input
              type="text"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={t("sourceForm.remarkPlaceholder")}
              maxLength={500}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-gray-400"
            />
            <p className="text-[10px] text-gray-400">{t("sourceForm.remarkHint")}</p>
          </div>

          {/* Loading overlay for meta data */}
          {loadingMeta && (
            <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center gap-2">
              <i className="ri-loader-4-line text-amber-500 text-sm animate-spin"></i>
              <span className="text-xs text-amber-700">{t("sourceForm.loadingMeta")}</span>
            </div>
          )}

          {/* Document Categories */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("sourceForm.docCategories")}
              </label>
              <FieldHelpTooltip text={t("sourceForm.docCategoriesTooltip")} />
            </div>
            <SimpleMultiSelect
              label=""
              values={docCategories}
              options={categoryOptions}
              onChange={setDocCategories}
              placeholder={t("sourceForm.docCategoriesPlaceholder")}
              hideLabel
            />
            {categoryOptions.length === 0 && !loadingMeta && (
              <p className="text-[11px] text-amber-600">{t("sourceForm.sourceTypeEmpty")}</p>
            )}
          </div>

          {/* Document Tags */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">{t("sourceForm.docTags")}</label>
              <FieldHelpTooltip text={t("sourceForm.docTagsTooltip")} />
            </div>
            <div className="flex flex-col gap-3">
              {docTagGroups.map((group, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-40 flex-shrink-0">
                    <SimpleSelect
                      label=""
                      value={group.tagType}
                      options={tagOptions}
                      onChange={(val) => updateDocTagGroup(index, val, group.tags)}
                      placeholder={t("sourceForm.docTagsSelectTag")}
                      hideLabel
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    {(() => {
                      if (hasPresetValues(group.tagType)) {
                        return (
                          <SimpleMultiSelect
                            label=""
                            values={group.tags}
                            options={getPresetValues(group.tagType)}
                            onChange={(vals) => updateDocTagGroup(index, group.tagType, vals)}
                            placeholder={t("sourceForm.docTagsSelectValues", {
                              tag: group.tagType.toLowerCase(),
                            })}
                            hideLabel
                            creatable
                          />
                        );
                      }
                      return (
                        <CreatableMultiSelect
                          label=""
                          values={group.tags}
                          options={[]}
                          onChange={(vals) => updateDocTagGroup(index, group.tagType, vals)}
                          placeholder={
                            group.tagType && tagPrompts[group.tagType]
                              ? `e.g. ${tagPrompts[group.tagType].split("\u3002")[0].slice(0, 60)}...`
                              : t("sourceForm.docTagsTagValues")
                          }
                          hideLabel
                          hideClearInDropdown
                        />
                      );
                    })()}
                    {group.tagType && tagPrompts[group.tagType] && (
                      <p className="text-[10px] text-gray-400 leading-relaxed px-0.5">
                        {tagPrompts[group.tagType]}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDocTagGroup(index)}
                    className={`${COMPACT_REMOVE_BUTTON_CLASS} mt-[9px]`}
                    title={t("sourceForm.docTagsRemoveGroup")}
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addDocTagGroup}
                className={`${INLINE_ADD_BUTTON_CLASS} w-fit`}
              >
                <i className="ri-add-line text-sm leading-none"></i>
                <span>{t("sourceForm.docTagsAddTag")}</span>
              </button>
              {tagOptions.length === 0 && !loadingMeta && (
                <p className="text-[11px] text-amber-600">{t("sourceForm.docTagsEmpty")}</p>
              )}
            </div>
          </div>

          {/* Source Type — data from Document Category Management */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("pathSettings.sourceType")}
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <FieldHelpTooltip text={t("pathSettings.sourceTypeTooltip")} />
            </div>
            <SimpleSelect
              label=""
              value={sourceType}
              options={typeOptions}
              onChange={(name) =>
                setSourceTypeId(
                  categoryCatalog.find((category) => category.name === name)?.id ?? "",
                )
              }
              placeholder={t("sourceForm.sourceTypePlaceholder")}
              hideLabel
            />
            {typeOptions.length === 0 && !loadingMeta && (
              <p className="text-[11px] text-amber-600">{t("sourceForm.sourceTypeEmpty")}</p>
            )}
          </div>

          {/* Agent Tips */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("sourceForm.agentTips")}
              </label>
              <FieldHelpTooltip text={t("sourceForm.agentTipsTooltip")} />
            </div>
            <textarea
              value={agentTips}
              onChange={(e) => setAgentTips(e.target.value)}
              placeholder={t("sourceForm.agentTipsPlaceholder")}
              rows={4}
              maxLength={500}
              className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 bg-white resize-none transition-colors"
            />
            <p className="text-[10px] text-gray-400">{t("sourceForm.agentTipsHint")}</p>
          </div>

          {/* Drive Input */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              {t("pathSettings.sourceDrive")}
            </label>
            <input
              type="text"
              value={drive}
              onChange={(e) => setDrive(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              placeholder={t("pathSettings.sourceDrivePlaceholder")}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-gray-400 font-mono"
            />
            <p className="text-[10px] text-gray-400 leading-snug">
              {t("pathSettings.sourceDriveHint")}
            </p>
          </div>

          {/* Included Paths Input */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("pathSettings.includedPaths")}
                <span className="ml-0.5 text-red-500">*</span>
              </label>
              <FieldHelpTooltip text={t("pathSettings.includedPathsHint")} />
            </div>
            <PathInputList
              label={t("pathSettings.includedPaths")}
              values={includedPathFields}
              placeholder={t("pathSettings.includedPathsPlaceholder")}
              addLabel={t("pathSettings.addPathField")}
              removeLabel={t("pathSettings.removePathField")}
              onChange={setIncludedPathFields}
            />
            {(!hasIncludedPath || invalidIncludedPath || error) && (
              <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
                {!hasIncludedPath
                  ? t("pathSettings.includedPathRequired")
                  : invalidIncludedPath
                    ? t("pathSettings.pathMustStartWithSlash")
                    : error}
              </p>
            )}
          </div>

          {/* Excluded Paths Input */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("pathSettings.excludedPaths")}
              </label>
              <FieldHelpTooltip text={t("pathSettings.excludedPathsHint")} />
            </div>
            <PathInputList
              label={t("pathSettings.excludedPaths")}
              values={excludedPathFields}
              placeholder={t("pathSettings.excludedPathsPlaceholder")}
              addLabel={t("pathSettings.addPathField")}
              removeLabel={t("pathSettings.removePathField")}
              hasConflict={!!pathConflict}
              onChange={setExcludedPathFields}
            />
            {invalidExcludedPath && (
              <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
                {t("pathSettings.pathMustStartWithSlash")}
              </p>
            )}
            {pathConflictMessage && (
              <p className="flex items-start gap-1.5 text-xs font-medium leading-relaxed text-red-500">
                <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                <span>{pathConflictMessage}</span>
              </p>
            )}
          </div>

          {/* Region */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("sourceForm.region")}
                <span className="ml-0.5 text-red-500">*</span>
              </label>
              <FieldHelpTooltip text={t("sourceForm.regionTooltip")} />
            </div>
            <SourceRegionSelector region={region} name="source-region-modal" onChange={setRegion} />
          </div>

          {/* User Scope */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("sourceForm.userScope")}
              </label>
              <FieldHelpTooltip text={t("sourceForm.userScopeTooltip")} />
            </div>
            <div
              role="radiogroup"
              aria-label={t("sourceForm.userScope")}
              className="flex flex-wrap gap-3"
            >
              {(
                [
                  { value: 1, label: t("sourceForm.userScope.internalExternal") },
                  { value: 0, label: t("sourceForm.userScope.internalOnly") },
                ] as Array<{ value: SourcePathAskUserScope; label: string }>
              ).map((option) => {
                const selected = userScope === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex min-w-52 flex-1 cursor-pointer items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm transition-colors ${
                      selected
                        ? "border-[#830051]/40 bg-[#830051]/5 text-gray-800"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="source-user-scope"
                      value={option.value}
                      checked={selected}
                      onChange={() => setUserScope(option.value)}
                      className="h-4 w-4 accent-[#830051]"
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Prerequisite Warning */}
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <i className="ri-error-warning-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-xs font-medium text-amber-800">
                  {t("pathSettings.prereq.warningTitle")}
                </p>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                  {t("pathSettings.prereq.warningDesc")}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 px-1">
              <div className="flex items-center gap-2.5 text-xs text-gray-500">
                <i className="ri-checkbox-circle-line text-gray-300 text-sm flex-shrink-0"></i>
                <span>{t("pathSettings.prereq.checkPermission")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onCancel}
            disabled={submitting}
            className={`${MODAL_FOOTER_CANCEL_BUTTON_CLASS} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {t("pathSettings.prereq.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`${MODAL_FOOTER_PRIMARY_BUTTON_CLASS} inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed`}
            style={MODAL_FOOTER_PRIMARY_BUTTON_STYLE}
          >
            {submitting && <i className="ri-loader-4-line inline-block animate-spin"></i>}
            {mode === "edit" ? t("pathSettings.save") : t("pathSettings.add")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PathSettingsPage({
  pageMode = "list",
}: {
  pageMode?: "list" | "add" | "edit";
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { sourceId } = useParams<{ sourceId: string }>();
  const [paths, setPaths] = useState<PathItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterDocCategory, setFilterDocCategory] = useState("all");
  const [filterDocTag, setFilterDocTag] = useState("all");
  const [filterUserScope, setFilterUserScope] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterSourceType, setFilterSourceType] = useState("all");
  const [page, setPage] = useState(1);
  const [editError, setEditError] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [loading, setLoading] = useState(pageMode !== "add");
  const [actionError, setActionError] = useState("");
  const [addPathError, setAddPathError] = useState("");
  const [addPathSubmitting, setAddPathSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string } | null>(null);
  const [globalExcludedKeywords, setGlobalExcludedKeywords] = useState<string[]>([]);
  const [globalKeywordsLoading, setGlobalKeywordsLoading] = useState(false);
  const [globalKeywordsLoadError, setGlobalKeywordsLoadError] = useState("");

  const fetchPaths = useCallback(async () => {
    setLoading(true);
    setActionError("");
    try {
      const res = await listSourcePathAsks();
      setPaths((res.data.Data ?? []).map(normalizePath));
    } catch {
      setActionError(t("pathSettings.loadFailed"));
      setPaths([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (pageMode === "add") return;
    void fetchPaths();
  }, [fetchPaths, pageMode]);

  const fetchGlobalKeywords = useCallback(async () => {
    setGlobalKeywordsLoading(true);
    setGlobalKeywordsLoadError("");
    try {
      const res = await getGlobalExcludedKeywords();
      setGlobalExcludedKeywords(res.data.Keywords ?? []);
    } catch {
      setGlobalKeywordsLoadError(t("pathSettings.globalKeywords.loadFailed"));
    } finally {
      setGlobalKeywordsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (pageMode !== "list") return;
    void fetchGlobalKeywords();
  }, [fetchGlobalKeywords, pageMode]);

  const editTarget =
    pageMode === "edit" && sourceId ? (paths.find((path) => path.id === sourceId) ?? null) : null;

  const handleSaveGlobalKeywords = async (keywords: string[]) => {
    const res = await updateGlobalExcludedKeywords(keywords);
    setGlobalExcludedKeywords(res.data.Keywords ?? []);
  };

  const handleAddPathConfirm = async (payload: SourceFormPayload) => {
    setAddPathError("");
    setAddPathSubmitting(true);
    try {
      const res = await createSourcePathAsk({
        SourceSite: payload.site,
        SourceDrive: payload.drive || undefined,
        IncludedPaths: payload.includedPaths,
        ExcludedPaths: payload.excludedPaths,
        SourceType: payload.sourceTypeId,
        DocCategoryIds: payload.docCategoryIds,
        DocTagBindings: payload.docTagBindings.map((b) => ({
          TagId: b.tagId,
          PresetValues: b.presetValues,
        })),
        Remark: payload.remark,
        AgentTips: payload.agentTips,
        Region: payload.region,
        UserScope: payload.userScope,
      });
      setPaths((prev) => [...prev, normalizePath(res.data)]);
      message.success(t("pathSettings.createSuccess"));
      navigate("/admin/settings/general-source");
    } catch (error) {
      setAddPathError(getResponseMessage(error) ?? t("pathSettings.saveFailed"));
    } finally {
      setAddPathSubmitting(false);
    }
  };

  const handleDeletePath = async (id: string) => {
    if (deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      await deleteSourcePathAsk(id);
      setPaths((prev) => prev.filter((path) => path.id !== id));
      setDeleteTarget(null);
      message.success(t("pathSettings.deleteSuccess"));
    } catch (error) {
      setActionError(getResponseMessage(error) ?? t("pathSettings.deleteFailed"));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleEditConfirm = async (payload: SourceFormPayload) => {
    if (!editTarget) return;
    setEditError("");
    setEditSubmitting(true);
    try {
      const res = await updateSourcePathAsk({
        ID: editTarget.id,
        SourceSite: payload.site,
        SourceDrive: payload.drive || undefined,
        IncludedPaths: payload.includedPaths,
        ExcludedPaths: payload.excludedPaths,
        SourceType: payload.sourceTypeId,
        DocCategoryIds: payload.docCategoryIds,
        DocTagBindings: payload.docTagBindings.map((b) => ({
          TagId: b.tagId,
          PresetValues: b.presetValues,
        })),
        Remark: payload.remark,
        AgentTips: payload.agentTips,
        Region: payload.region,
        UserScope: payload.userScope,
      });
      const updatedPath = normalizePath(res.data);
      setPaths((prev) => prev.map((path) => (path.id === updatedPath.id ? updatedPath : path)));
      message.success(t("pathSettings.updateSuccess"));
      navigate("/admin/settings/general-source");
    } catch (error) {
      setEditError(getResponseMessage(error) ?? t("pathSettings.saveFailed"));
    } finally {
      setEditSubmitting(false);
    }
  };

  const [copiedId, setCopiedId] = useState<string | null>(null);

  // 列表页只存 ID，展示与筛选都要现查名称。分类 / 标签改名后此处自动跟随。
  const [categoryNameById, setCategoryNameById] = useState<Record<string, string>>({});
  const [tagNameById, setTagNameById] = useState<Record<string, string>>({});

  useEffect(() => {
    if (pageMode !== "list") return;
    let cancelled = false;
    void (async () => {
      const [categories, tags] = await Promise.all([docCategoryApi.list(), docTagApi.list()]);
      if (cancelled) return;
      setCategoryNameById(Object.fromEntries(categories.map((c) => [c.id, c.name])));
      setTagNameById(Object.fromEntries(tags.map((tg) => [tg.id, tg.name])));
    })();
    return () => {
      cancelled = true;
    };
  }, [pageMode]);

  /** 已删除的分类 / 标签查不到名称，展示与筛选中一并跳过 */
  const categoryNames = (ids: string[]) =>
    ids.map((id) => categoryNameById[id]).filter((n): n is string => !!n);
  const tagNames = (bindings: Array<{ tagId: string }>) =>
    bindings.map((b) => tagNameById[b.tagId]).filter((n): n is string => !!n);
  const sourceTypeName = (sourceTypeId: string) => categoryNameById[sourceTypeId] ?? "";

  const allSourceTypes = [
    ...new Set(paths.map((p) => sourceTypeName(p.sourceTypeId)).filter(Boolean)),
  ].sort();
  const allDocCategories = [
    ...new Set(paths.flatMap((p) => categoryNames(p.docCategoryIds))),
  ].sort();
  const allDocTagTypes = [...new Set(paths.flatMap((p) => tagNames(p.docTagBindings)))].sort();

  const filteredPaths = paths.filter((path) => {
    const matchSearch =
      searchQuery.trim() === "" ||
      path.site.toLowerCase().includes(searchQuery.toLowerCase()) ||
      path.drive.toLowerCase().includes(searchQuery.toLowerCase()) ||
      path.includedPaths.some((value) => value.toLowerCase().includes(searchQuery.toLowerCase())) ||
      path.excludedPaths.some((value) => value.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchUserScope = filterUserScope === "all" || String(path.userScope) === filterUserScope;
    const matchRegion = filterRegion === "all" || path.region === filterRegion;
    const matchSourceType =
      filterSourceType === "all" || sourceTypeName(path.sourceTypeId) === filterSourceType;
    const matchDocCategory =
      filterDocCategory === "all" || categoryNames(path.docCategoryIds).includes(filterDocCategory);
    const matchDocTag =
      filterDocTag === "all" || tagNames(path.docTagBindings).includes(filterDocTag);

    return (
      matchSearch &&
      matchUserScope &&
      matchRegion &&
      matchSourceType &&
      matchDocCategory &&
      matchDocTag
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredPaths.length / SOURCE_LIST_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedPaths = filteredPaths.slice(
    (currentPage - 1) * SOURCE_LIST_PAGE_SIZE,
    currentPage * SOURCE_LIST_PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [
    filterDocCategory,
    filterDocTag,
    filterRegion,
    filterSourceType,
    filterUserScope,
    searchQuery,
  ]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  if (pageMode === "add") {
    return (
      <AdminLayout>
        <AddPathModal
          key="general-source-add"
          presentation="page"
          error={addPathError}
          submitting={addPathSubmitting}
          onConfirm={(payload) => void handleAddPathConfirm(payload)}
          onCancel={() => {
            if (addPathSubmitting) return;
            setAddPathError("");
            navigate("/admin/settings/general-source");
          }}
        />
      </AdminLayout>
    );
  }

  if (pageMode === "edit") {
    return (
      <AdminLayout>
        {loading ? (
          <div className="px-6 py-20 text-center text-sm text-gray-400">
            <i className="ri-loader-4-line mb-2 inline-block animate-spin text-2xl" />
            <div>{t("pathSettings.loading")}</div>
          </div>
        ) : actionError ? (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-4 text-sm text-red-600">
            <span>{actionError}</span>
            <button
              type="button"
              className="ml-3 font-medium underline"
              onClick={() => void fetchPaths()}
            >
              {t("common.retry")}
            </button>
          </div>
        ) : !editTarget ? (
          <div className="px-6 py-20 text-center text-sm text-gray-500">
            <div>{t("pathSettings.sourceNotFound")}</div>
            <button
              type="button"
              className="mt-3 font-medium text-[#830051] underline"
              onClick={() => navigate("/admin/settings/general-source")}
            >
              {t("common.back")}
            </button>
          </div>
        ) : (
          <AddPathModal
            key={`general-source-edit-${editTarget.id}`}
            mode="edit"
            presentation="page"
            initialSite={editTarget.site}
            initialDrive={editTarget.drive}
            initialIncludedPaths={editTarget.includedPaths}
            initialExcludedPaths={editTarget.excludedPaths}
            initialDocCategoryIds={editTarget.docCategoryIds}
            initialDocTagBindings={editTarget.docTagBindings}
            initialSourceTypeId={editTarget.sourceTypeId}
            initialRemark={editTarget.remark}
            initialAgentTips={editTarget.agentTips}
            initialRegion={editTarget.region}
            initialUserScope={editTarget.userScope}
            error={editError}
            submitting={editSubmitting}
            onConfirm={(payload) => void handleEditConfirm(payload)}
            onCancel={() => {
              if (editSubmitting) return;
              setEditError("");
              navigate("/admin/settings/general-source");
            }}
          />
        )}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-5 flex items-start justify-between">
        <div>
          <AdminPageTitle title={t("pathSettings.title")} tooltip={t("pathSettings.subtitle")} />
        </div>
        <button
          onClick={() => navigate("/admin/settings/general-source/add")}
          className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-lg cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
          style={{ backgroundColor: "#830051" }}
        >
          <i className="ri-add-line text-base"></i>
          {t("pathSettings.addPath")}
        </button>
      </div>

      <GlobalExcludedKeywords
        keywords={globalExcludedKeywords}
        loading={globalKeywordsLoading}
        loadError={globalKeywordsLoadError}
        onRetry={() => void fetchGlobalKeywords()}
        onSave={handleSaveGlobalKeywords}
      />

      {actionError && (
        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {actionError}
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <select
              value={filterDocCategory}
              onChange={(e) => setFilterDocCategory(e.target.value)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">{t("pathSettings.filter.allDocCategories")}</option>
              {allDocCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>
          <div className="relative">
            <select
              value={filterDocTag}
              onChange={(e) => setFilterDocTag(e.target.value)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">{t("pathSettings.filter.allDocTags")}</option>
              {allDocTagTypes.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>
          <div className="relative">
            <select
              value={filterSourceType}
              onChange={(e) => setFilterSourceType(e.target.value)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">{t("pathSettings.filter.allSourceTypes")}</option>
              {allSourceTypes.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>
          <div className="relative">
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">{t("pathSettings.filter.allRegions")}</option>
              <option value="Mainland">{t("pathSettings.filter.mainland")}</option>
              <option value="HK">{t("pathSettings.filter.hongkong")}</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>
          <div className="relative">
            <select
              aria-label={t("sourceForm.userScope")}
              value={filterUserScope}
              onChange={(e) => setFilterUserScope(e.target.value)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">{t("pathSettings.filter.allScopes")}</option>
              <option value="1">{t("sourceForm.userScope.internalExternal")}</option>
              <option value="0">{t("sourceForm.userScope.internalOnly")}</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>
        </div>
        <AdminSearchForm
          value={searchInput}
          onChange={setSearchInput}
          onSearch={() => setSearchQuery(searchInput)}
          searchLabel={t("common.search")}
          placeholder={t("pathSettings.searchPlaceholder")}
          showReset={
            searchQuery !== "" ||
            searchInput !== "" ||
            filterUserScope !== "all" ||
            filterRegion !== "all" ||
            filterSourceType !== "all" ||
            filterDocCategory !== "all" ||
            filterDocTag !== "all"
          }
          onReset={() => {
            setSearchQuery("");
            setSearchInput("");
            setFilterUserScope("all");
            setFilterRegion("all");
            setFilterSourceType("all");
            setFilterDocCategory("all");
            setFilterDocTag("all");
          }}
          resetLabel={t("common.reset")}
        />
      </div>

      <div className="flex flex-col gap-5">
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1620px] table-auto font-sans text-sm">
              <colgroup>
                <col style={{ width: 200 }} />
                <col style={{ width: 140 }} />
                <col />
                <col />
                <col style={{ width: 160 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 96 }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500">
                  <th className="px-5 py-3 text-left" style={{ minWidth: 200 }}>
                    {t("pathSettings.sourceSite")}
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 140 }}>
                    {t("pathSettings.sourceDrive")}
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 240 }}>
                    {t("pathSettings.includedPaths")}
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 200 }}>
                    {t("pathSettings.excludedPaths")}
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 160 }}>
                    {t("pathSettings.docCategories")}
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 180 }}>
                    {t("pathSettings.docTags")}
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 140 }}>
                    Type
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 120 }}>
                    Region
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 150 }}>
                    {t("sourceForm.userScope")}
                  </th>
                  <th
                    className="px-5 py-3 text-right sticky right-0 z-20 bg-gray-50 border-l border-gray-100"
                    style={{ minWidth: 96 }}
                  >
                    {t("pathSettings.operation")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center text-sm text-gray-400">
                      <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2"></i>
                      <div>{t("common.loading")}</div>
                    </td>
                  </tr>
                ) : filteredPaths.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-5 py-6 text-center text-sm text-gray-300">
                      {t("pathSettings.emptyPaths")}
                    </td>
                  </tr>
                ) : (
                  paginatedPaths.map((path) => (
                    <tr
                      key={path.id}
                      className="group hover:bg-gray-50 transition-colors align-top"
                    >
                      <td className="px-5 py-3 text-sm text-gray-900 font-medium truncate">
                        <div className="flex-1 flex items-center gap-2">
                          <Tooltip title={path.site}>
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {path.site}
                            </span>
                          </Tooltip>
                          <button
                            type="button"
                            title={copiedId === path.id ? "Copied!" : "Copy SharePoint link"}
                            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = formatSharePointSitePreview(path.site);
                              navigator.clipboard.writeText(url);
                              setCopiedId(path.id);
                              setTimeout(
                                () => setCopiedId((prev) => (prev === path.id ? null : prev)),
                                1500,
                              );
                            }}
                          >
                            <i
                              className={`text-sm ${copiedId === path.id ? "ri-check-line text-green-600" : "ri-file-copy-line"}`}
                            ></i>
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm truncate">
                        {path.drive ? (
                          <span className="text-gray-600">{path.drive}</span>
                        ) : (
                          <span className="text-gray-300">{t("pathSettings.allDrives")}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 break-all">
                        <PathListDisplay paths={path.includedPaths} className="bg-gray-100" />
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 break-all">
                        {path.excludedPaths.length > 0 ? (
                          <PathListDisplay paths={path.excludedPaths} className="bg-red-50" />
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>

                      {/* Doc Categories */}
                      <td className="px-5 py-3 align-top">
                        {categoryNames(path.docCategoryIds).length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1">
                            {categoryNames(path.docCategoryIds).map((category) => (
                              <span
                                key={category}
                                className="inline-flex items-center text-[10px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5"
                              >
                                {category}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>

                      {/* Doc Tags */}
                      <td className="px-5 py-3 align-top">
                        {tagNames(path.docTagBindings).length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1">
                            {tagNames(path.docTagBindings).map((docTag) => (
                              <span
                                key={docTag}
                                className="inline-flex items-center text-[10px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5"
                              >
                                {docTag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-5 py-3 align-top">
                        <span className="text-xs text-gray-600">
                          {sourceTypeName(path.sourceTypeId) || "-"}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-top">
                        <span className="text-xs text-gray-600">{path.region}</span>
                      </td>
                      {/* User Scope */}
                      <td className="px-5 py-3 align-top">
                        <span className="text-xs text-gray-600">
                          {path.userScope === 1
                            ? t("sourceForm.userScope.internalExternal")
                            : t("sourceForm.userScope.internalOnly")}
                        </span>
                      </td>

                      <td className="px-5 py-3 sticky right-0 z-10 bg-white group-hover:bg-gray-50 border-l border-gray-100 transition-colors">
                        <div className="flex items-center gap-1.5 justify-end">
                          <button
                            type="button"
                            aria-label={`${t("pathSettings.editTitle")}: ${path.site}`}
                            onClick={() =>
                              navigate(
                                `/admin/settings/general-source/${encodeURIComponent(path.id)}/edit`,
                              )
                            }
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                          >
                            <i className="ri-pencil-line text-sm text-gray-400"></i>
                          </button>
                          <button
                            onClick={() => setDeleteTarget({ id: path.id })}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 cursor-pointer transition-colors"
                          >
                            <i className="ri-delete-bin-line text-sm text-red-400"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <AdminPagination
        total={filteredPaths.length}
        page={currentPage}
        pageSize={SOURCE_LIST_PAGE_SIZE}
        loading={loading}
        onPageChange={(nextPage) => setPage(nextPage)}
      />

      {deleteTarget && (
        <ConfirmDialog
          title={t("pathSettings.deleteConfirmTitle")}
          description={t("pathSettings.deleteConfirmDesc")}
          confirmLabel={t("pathSettings.delete")}
          confirmDanger
          submitting={deleteSubmitting}
          onConfirm={() => void handleDeletePath(deleteTarget.id)}
          onCancel={() => {
            if (!deleteSubmitting) setDeleteTarget(null);
          }}
        />
      )}
    </AdminLayout>
  );
}
